import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { isEmptyField } from 'src/app/main/shared/stock-chart-module/stock-chart/stock-chart-shared/stock-chart-shared-services/stock-chart-util.service';
import { ProjConstants } from 'src/app/utils/proj.constant';
import { BASIN_HIE, ECMWF_SEASONAL_COMPONENT_TYPES, ECMWF_SOURCE_TYPES, RAINFALL_LEGEND_LIST, RainfallForecastMapConstants, getTrendYearColor } from './rainfall-forecast-map/rainfall-forecast-map-constants';
import { AgricultureMapConstants } from 'src/app/main/constants/dims-map-constants';
import { environment } from 'src/environments/environment';
import { isNullOrUndefined } from 'util';
import { UtilsService } from 'src/app/utils/utils.service';

@Injectable({
  providedIn: 'root'
})
export class RainfallForecastDataService {
  sourceSelection: any;

  constructor(private http: HttpClient, private utils: UtilsService) { }

  getRainfallForecastData(postData) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getTableData'
    return this.http.post<any>(url, postData)
  }

  getHeirarchy(postData) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + "locations/heirarchy";
    return this.http.post<any>(url, postData)
  }

  getRainfallForecastChartData(postData) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + "weatherForecast/getChartData";
    return this.http.post<any>(url, postData)
  }

  getGoogleTableData(postData) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getWeatherNextTableData'
    return this.http.post<any>(url, postData)
  }

  getGoogleChartData(postData) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + "weatherForecast/getWeatherNextChartData";
    return this.http.post<any>(url, postData)
  }

  getSeasonalTableData(postData) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getSeasonalTableData'
    return this.http.post<any>(url, postData)
  }

  getSeasonalChartData(postData) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + "weatherForecast/getSeasonalChartData";
    return this.http.post<any>(url, postData)
  }

  getAgriMaskPercentageData(postData) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + "agri/agriMaskArea";
    return this.http.post<any>(url, postData)
  }

  getRfCategoryDistribution(postData) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + "weatherForecast/getRfCategoryDistribution";
    return this.http.post<any>(url, postData)
  }

  getWeeklyAccumulatedData(postData) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getWeeklyAccumulatedData';
    return this.http.post<any>(url, postData)
  }

  getWeeklyHeatMapData(subTimePeriod: number) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getWeeklyHeatMapData/' + subTimePeriod;
    return this.http.get<any>(url)
  }

  getWeeklyChartData(postData) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getWeeklyChartData';
    return this.http.post<any>(url, postData)
  }

  getImdStateRainfallData(postData: any) {
    return this.http.post(ProjConstants.API_URL + 'rainfall/getImdStateRainfallData', postData);
  }

  // -7D Stations view — reuses the same rf/aggregated endpoint and IMD_STATIONS classification
  // as RainfallActualDataService.post()/getPostDataForSensor().
  getStationsData(postData: any) {
    return this.http.post<any>(environment.url.HEATWAVE_STAGING_API_URL + 'rf/aggregated', postData);
  }

  getStationsPostData(queryParams: any, dateStrYYYYMMDD: any) {
    let stateUUID: any;
    if (queryParams.locType == 'DISTRICT') {
      const allLocations = queryParams.location ? queryParams.location.split('&') : [];
      stateUUID = allLocations[1] ? allLocations[1].split('##')[1] : queryParams.pUUID;
    }
    return {
      sDate: dateStrYYYYMMDD,
      eDate: dateStrYYYYMMDD,
      pUUID: queryParams.locType == 'DISTRICT' ? stateUUID : queryParams.pUUID,
      pType: queryParams.locType == 'DISTRICT' ? 'STATE' : queryParams.locType,
      format: 'DAILY',
      cType: 'RAINFALL',
      pEUUID: queryParams.locType == 'DISTRICT' ? stateUUID : (queryParams.pEUUID || queryParams.pUUID),
      classification: ['ACTUAL'],
      src: 'MERGED_STATIONS',
      aggr: 'SUM',
      includeAllLoc: true
    };
  }

  getMergedStationsHeatmapForDate(postData: { sDate: string, eDate: string }) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getMergedStationsHeatmaps';
    return this.http.post<any>(url, postData);
  }

  getLast7DaysData(postData) {
    const url = environment.url.OBSERVED_API_URL + 'rf/aggregated/last-n-days';
    return this.http.post<any>(url, postData);
  }

  getLast7DaysChartData(postData) {
    const url = environment.url.OBSERVED_API_URL + 'rf/chart/last-n-days';
    return this.http.post<any>(url, postData);
  }

  getLast7DaysTimeStamps() {
    const today = new Date();
    const blocks: any = {};
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() - (6 - i));
      d.setHours(0, 0, 0, 0);
      const dateStr = this.formatYYYYMMDD(d);
      blocks[i] = {
        timeStamp: d.getTime(),
        dateStr: dateStr,
        timeLineName: { label: this.formatDMY(d.getTime()), toolTip: this.convetTimeStampToDay(d.getTime()) },
        leftSideName: this.formatDMY(d.getTime()),
        timeLinePos: i
      };
    }
    return blocks;
  }

  get24HrsTimeStamps() {
    // ---------------33435 IMD Block---------------
    let hoursIncrement = 0;
    let startHour = 8;
    // if (source == 'IMD') {
    //   hoursIncrement = ProjConstants.SOURCE_TIMESTAMP_DIFF.IMD;
    //   startHour = 8;
    // }
    const today = new Date();
    const refTs = today.getTime();
    // let refTs = ProjConstants.RefTs24;
    const start = new Date(refTs);
    start.setHours(startHour, 30, 0, 0);
    // The 8 × 3hr window runs 08:30 → next day's 08:30. Between 00:00 and 08:30 "today's" 08:30
    // is still in the future, so getCurrentBlockTs() finds no block <= now and returns undefined,
    // turning every block timestamp into NaN. Anchor on the previous day's 08:30 in that case so
    // e.g. 01:00 on 24 Sep resolves to the 23 Sep 23:30 block.
    if (start.getTime() > refTs) {
      start.setDate(start.getDate() - 1);
    }
    const dayStartTs = start.getTime();
    const blockIndex = Math.floor((refTs - dayStartTs) / 10800000);
    let startTimestamp = this.getCurrentBlockTs(dayStartTs)

    const blocks:any = {};
    for (let i = 0; i < 8; i++) {
      blocks[i] = {};
      // blocks[i]['timeStamp'] = dayStartTs + ((i + blockIndex) * 10800000);
      blocks[i]['timeStamp'] = startTimestamp + ((i * 10800000));
      blocks[i]['timeLineName'] = {};
      blocks[i]['timeLineName']['toolTip'] = this.convertTimestampToFullBlockValue(blocks[i]['timeStamp']);
      blocks[i]['timeLineName']['label'] = this.convertTimestampToBlockValue(blocks[i]['timeStamp']);
      blocks[i]['leftSideName'] = this.convertTimestampToFullBlockValue(blocks[i]['timeStamp']);
      blocks[i]['timeLinePos'] = i;
    }
    let index = this.getCurrentBlockIndex(blocks)
    // console.log("BLOCKS::",blocks,index);
    blocks[8] = {};
    blocks[8]['timeStamp'] = blocks[index]['timeStamp'];
    blocks[8]['timeLineName'] = {};
    blocks[8]['timeLineName']['toolTip'] = this.convertTimestampToBlockValue(blocks[index]['timeStamp']) + '-' + this.convertTimestampToBlockValue(blocks[index]['timeStamp']);
    blocks[8]['timeLineName']['label'] = '24hr';
    blocks[8]['leftSideName'] = '24hr';
    blocks[8]['timeLinePos'] = 8;
    return blocks;
  }
  //  get10DaysTimeStamps(source, timestamp) {
  //   let hoursIncrement = 0;
  //   let startHour;
  //   if (timestamp.endsWith('00')) {
  //     startHour = 8
  //   } else {
  //     startHour = 20
  //   }
  //   // if (source == 'IMD') {
  //   //   hoursIncrement = ProjConstants.SOURCE_TIMESTAMP_DIFF.IMD;
  //   //   startHour = 8;
  //   // }
  //   const today = new Date();
  //   const refTs = today.getTime();
  //   // let refTs = ProjConstants.RefTs24;
  //   const start = new Date(refTs);
  //   start.setHours(startHour, 30, 0, 0);
  //   const dayStartTs = start.getTime();
  //   const blocks = {};
  //   for (let i = 0; i < 7; i++) {
  //     blocks[i] = {};
  //     // blocks[i]['timeStamp'] = dayStartTs + ((i) * 86400000);
  //     blocks[i]['timeStamp'] = dayStartTs + ((i) * 86400000) - hoursIncrement;
  //     blocks[i]['timeLineName'] = {};
  //     blocks[i]['timeLineName']['label'] = this.convetTimeStampToDay(blocks[i]['timeStamp'] + hoursIncrement);
  //     blocks[i]['timeLineName']['toolTip'] = source == 'IMD' ? '8:30 AM - 8:30 AM' : '5:30 AM - 5:30 AM';
  //     blocks[i]['leftSideName'] = this.convetTimeStampToDay(blocks[i]['timeStamp'] + hoursIncrement);
  //     blocks[i]['timeLinePos'] = i;
  //   }
  //   return blocks;
  // }

  get10DaysTimeStamps(source, timestamp, count, format?) {
    let hoursIncrement = 0;
    const forecastDate = this.getForecastDateFromTimestamp(timestamp, format);
    const startHour = this.getForecastStartHour(timestamp);
    // if (source == 'IMD') {
    //   hoursIncrement = ProjConstants.SOURCE_TIMESTAMP_DIFF.IMD;
    //   startHour = 8;
    // }
    const refTs = forecastDate.getTime();
    // let refTs = ProjConstants.RefTs24;
    const start = new Date(refTs);
    start.setHours(startHour, 30, 0, 0);
    const dayStartTs = start.getTime();
    const blocks = {};
    for (let i = 0; i < count; i++) {
      blocks[i] = {};
      // blocks[i]['timeStamp'] = dayStartTs + ((i) * 86400000);
      blocks[i]['timeStamp'] = dayStartTs + ((i) * 86400000) - hoursIncrement;
      blocks[i]['timeLineName'] = {};
      blocks[i]['timeLineName']['label'] = format ? this.formatShortDate(blocks[i]['timeStamp']) : this.convetTimeStampToDay(blocks[i]['timeStamp'] + hoursIncrement);
      blocks[i]['timeLineName']['toolTip'] = source == 'IMD' ? '8:30 AM - 8:30 AM' : '5:30 AM - 5:30 AM';
      blocks[i]['leftSideName'] = this.convetTimeStampToDay(blocks[i]['timeStamp'] + hoursIncrement);
      blocks[i]['timeLinePos'] = i;
    }
    return blocks;
  }

  getForecastDateFromTimestamp(timestamp, format?) {
    const timestampStr = timestamp ? timestamp.toString() : '';
    const yyyymmddhhRegex = /^\d{10}$/;

    if (yyyymmddhhRegex.test(timestampStr)) {
      const year = Number(timestampStr.substring(0, 4));
      const month = Number(timestampStr.substring(4, 6)) - 1;
      const day = Number(timestampStr.substring(6, 8));
      const forecastDate = new Date(year, month, day);
      forecastDate.setDate(forecastDate.getDate() + 1);
      return forecastDate;
    }

    const timestampNumber = Number(timestamp);
    if (!isNaN(timestampNumber) && timestampStr.length >= 12) {
      return new Date(timestampNumber);
    }

    return new Date();
  }

  getForecastStartHour(timestamp) {
    const timestampStr = timestamp ? timestamp.toString() : '';
    const yyyymmddhhRegex = /^\d{10}$/;

    if (yyyymmddhhRegex.test(timestampStr)) {
      const forecastHour = Number(timestampStr.substring(8, 10));
      return forecastHour >= 12 ? 20 : 8;
    }

    const timestampNumber = Number(timestamp);
    if (!isNaN(timestampNumber) && timestampStr.length >= 12) {
      const forecastHour = new Date(timestampNumber).getHours();
      return forecastHour >= 12 ? 20 : 8;
    }

    return 8;
  }

  get7DaysTimeStamps(source, timestamp) {
    const today = new Date();
    const start = new Date(today);
    if (source === 'WEATHER_NEXT_GRAPH') {
      // start.setDate(today.getDate() + 1);
      start.setHours(5, 30, 0, 0);
    } else {
      start.setHours(8, 30, 0, 0);
    }
    const dayStartTs = start.getTime();
    const blocks = {};
    for (let i = 0; i < 7; i++) {
      blocks[i] = {};
      blocks[i]['timeStamp'] = dayStartTs + ((i) * 86400000);
      blocks[i]['timeLineName'] = {};
      blocks[i]['timeLineName']['label'] = this.convetTimeStampToDay(blocks[i]['timeStamp']);
      blocks[i]['timeLineName']['toolTip'] = source === 'WEATHER_NEXT_GRAPH' ? '5:30 AM - 5:30 AM' : '8:30 AM - 8:30 AM';
      blocks[i]['leftSideName'] = this.convetTimeStampToDay(blocks[i]['timeStamp']);
      blocks[i]['timeLinePos'] = i;
    }
    return blocks;
  }

  convetTimeStampToDay(ts) {
    const date = new Date(ts).toLocaleDateString();
    return date;
  }

  formatDMY(ts) {
    const date = new Date(ts);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   * Builds the block list for the deterministic-playbar: a single combined block for the
   * already-loaded 14-day deterministic window, followed by 7-day weekly blocks covering the
   * next 31 days. Weekly blocks carry the exact YYYYMMDD date array the extended-daily heatmap
   * API expects, so they can be fetched on demand when the block is clicked.
   */
  getExtendedPlaybarBlocks(source, timestamp) {
    const detTsArray: any = this.get10DaysTimeStamps(source, timestamp, 14, true);
    const detStartTs = detTsArray[0]['timeStamp'];
    const detEndTs = detTsArray[13]['timeStamp'];

    const blocks: any[] = [{
      groupName: 'deterministic',
      type: 'deterministic',
      label: `${this.formatShortDate(detStartTs)} - ${this.formatShortDate(detEndTs)}`
    }];

    const EXTENDED_TOTAL_DAYS = 31;
    let dayOffset = 0;
    let weekIndex = 0;
    while (dayOffset < EXTENDED_TOTAL_DAYS) {
      const daysInBlock = Math.min(7, EXTENDED_TOTAL_DAYS - dayOffset);
      const dates: number[] = [];
      for (let d = 0; d < daysInBlock; d++) {
        dates.push(this.tsToYYYYMMDD(detEndTs + (dayOffset + 1 + d) * 86400000));
      }
      const blockStartTs = detEndTs + (dayOffset + 1) * 86400000;
      const blockEndTs = detEndTs + (dayOffset + daysInBlock) * 86400000;
      blocks.push({
        groupName: 'ext_week_' + weekIndex,
        type: 'weekly',
        label: `${this.formatShortDate(blockStartTs)} - ${this.formatShortDate(blockEndTs)}`,
        dates
      });
      dayOffset += daysInBlock;
      weekIndex++;
    }
    return blocks;
  }

  tsToYYYYMMDD(ts: number): number {
    const d = new Date(ts);
    return Number(`${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`);
  }

  yyyymmddToTs(yyyymmdd: any) {
    const s = yyyymmdd.toString();
    const year = Number(s.substring(0, 4));
    const month = Number(s.substring(4, 6)) - 1;
    const day = Number(s.substring(6, 8));
    return new Date(year, month, day).getTime();
  }

  formatShortDate(ts: number): string {
    return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  }

  getEcmwfExtendedDailyHeatMapData(dates: number[]) {
    const url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getEcmwfExtendedDailyHeatMapData';
    return this.http.post<any>(url, { date: dates });
  }

  getEcmwfExtendedDailyForecastData(postData) {
    const url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getEcmwfExtendedDailyForecastData';
    return this.http.post<any>(url, postData);
  }

  getEcmwfExtendedDailyChartData(postData) {
    const url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getEcmwfExtendedDailyChartData';
    return this.http.post<any>(url, postData);
  }

  /**
   * Post data for the deterministic-table extension window: the 31 days following the
   * already-fetched 14-day deterministic window (same window boundary as getExtendedPlaybarBlocks).
   */
  getExtendedDeterministicPostData(queryParams) {
    let hie: any = queryParams['view'] === 'Basin' ? BASIN_HIE : AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
    let cType = hie[hie.indexOf(queryParams.locType) + 1] || queryParams.locType;
    const detTsArray: any = this.get10DaysTimeStamps(queryParams['source'], queryParams.refTs, 14, true);
    const detEndTs = detTsArray[13]['timeStamp'];
    return {
      source: 'ecmwf_ext',
      pType: queryParams.locType,
      cType: cType,
      pUUID: queryParams.pUUID,
      startDate: this.tsToYYYYMMDD(detEndTs + 86400000),
      endDate: this.tsToYYYYMMDD(detEndTs + 31 * 86400000)
    };
  }

  /**
   * Merges the deterministic table response with the ecmwf_ext extension response
   * ({uuid: {dataMap: {YYYYMMDD: rf}, locName}}) into the same [{name, uuid, table_data}]
   * shape the primary response uses, so downstream formatting is untouched.
   */
  mergeDeterministicTableData(primaryRes: any[], extendedRaw: any) {
    if (!extendedRaw || Object.keys(extendedRaw).length === 0) {
      return primaryRes;
    }
    if (!primaryRes || primaryRes.length === 0) {
      return primaryRes;
    }
    const byUuid = new Map(primaryRes.map((item: any) => [item.uuid, item]));
    Object.keys(extendedRaw).forEach(uuid => {
      const existing = byUuid.get(uuid);
      if (!existing) {
        return;
      }
      const { dataMap } = extendedRaw[uuid];
      const extEntries = Object.keys(dataMap || {}).map(dateKey => ({
        timeStamp: this.yyyymmddToTs(dateKey),
        blockLength: 'ecmwf_ext',
        data: { rf: dataMap[dateKey] },
        alert: null
      }));
      existing.table_data = [...existing.table_data, ...extEntries];
    });
    return Array.from(byUuid.values());
  }

  /**
   * Merges the deterministic chart response with the ecmwf_ext extension chart response into a
   * single response of the same {bandName: {itemKey: {timestamp: value}}} shape, so the existing
   * setChartData formatting is untouched. The extension response keys its day values by YYYYMMDD
   * (e.g. "20260813") instead of epoch-ms timestamps, so they're converted before merging.
   */
  mergeDeterministicChartData(primaryRaw: any, extendedRaw: any) {
    if (!extendedRaw || Object.keys(extendedRaw).length === 0) {
      return primaryRaw;
    }
    if (!primaryRaw || Object.keys(primaryRaw).length === 0) {
      return primaryRaw;
    }
    const merged: any = {};
    Object.keys(primaryRaw).forEach(band => {
      merged[band] = this.deepClone(primaryRaw[band]) || {};
    });
    Object.keys(extendedRaw).forEach(band => {
      if (!merged[band]) {
        merged[band] = {};
      }
      const extItem = extendedRaw[band] || {};
      Object.keys(extItem).forEach(itemKey => {
        const dateMap = extItem[itemKey] || {};
        const tsMap: any = {};
        Object.keys(dateMap).forEach(dateKey => {
          tsMap[this.yyyymmddToTs(dateKey)] = dateMap[dateKey];
        });
        merged[band][itemKey] = { ...(merged[band][itemKey] || {}), ...tsMap };
      });
    });
    return merged;
  }

  getHeatMap(url) {
    let dataPromise = new Promise((resolve, reject) => {
      this.http.get(url).subscribe(
        data => {
          let obj = this.setMapData(data);
          resolve(obj);
        });
    });
    return dataPromise;
  }

  getGoogleHeatMap(ts, forecastLength, interval) {
    // Use the dedicated WeatherNext heatmap endpoint with 'default' instead of 'null'
    let url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getWeatherNextHeatMapData/WEATHER_NEXT_GRAPH/WEATHER_NEXT_GRAPH/RAINFALL/' + ts + '/' + forecastLength + '/' + interval + '/null';
    return new Promise((resolve, reject) => {
      this.http.get(url).subscribe(
        (data: any) => {
          let obj = this.setMapData(data);
          resolve(obj);
        },
        error => {
          console.error('Google heatmap fetch failed:', error);
          resolve({});
        }
      );
    });
  }

  /** Generates 4 x 6-hour time blocks for the +24D Google/WeatherNext playbar */
  get6HrGoogleTimeStamps() {
    const slotHours = [5, 11, 17, 23]; // matches getGooglePostData's slots
    const now = new Date();
    const refDate = new Date(now);

    let selectedHour: number | null = null;
    for (let i = slotHours.length - 1; i >= 0; i--) {
      const h = slotHours[i];
      const slotTime = new Date(now);
      slotTime.setHours(h, 30, 0, 0);
      if (now.getTime() >= slotTime.getTime()) {
        selectedHour = h;
        break;
      }
    }

    if (selectedHour === null) {
      refDate.setDate(refDate.getDate() - 1);
      refDate.setHours(23, 30, 0, 0);
    } else {
      refDate.setHours(selectedHour, 30, 0, 0);
    }

    const startTs = refDate.getTime();
    const blocks = {};
    for (let i = 0; i < 4; i++) {
      blocks[i] = {};
      blocks[i]['timeStamp'] = startTs + i * 6 * 3600 * 1000;
      blocks[i]['timeLineName'] = {};
      blocks[i]['timeLineName']['toolTip'] = this.convertTimestampToFullBlockValue(blocks[i]['timeStamp']);
      blocks[i]['timeLineName']['label'] = this.convertTimestampToBlockValue(blocks[i]['timeStamp']);
      blocks[i]['leftSideName'] = this.convertTimestampToFullBlockValue(blocks[i]['timeStamp']);
      blocks[i]['timeLinePos'] = i;
    }
    return blocks;
  }

  setMapData(data) {
    let obj = {};
    let imageUrl = {};
    let imageBounds = [];
    for (let block of Object.keys(data)) {
      for (let property of Object.keys(data[block])) {
        imageUrl = data[block]['heatMapImage'];
        let latlongs = [];
        latlongs.push(data[block]['heatMapImageBound']['southWest']['latitude']);
        latlongs.push(data[block]['heatMapImageBound']['southWest']['longitude']);
        imageBounds.push(latlongs);
        latlongs = [];
        latlongs.push(data[block]['heatMapImageBound']['northEast']['latitude']);
        latlongs.push(data[block]['heatMapImageBound']['northEast']['longitude']);
        imageBounds.push(latlongs);
      }
      obj['url'] = imageUrl;
      obj['bounds'] = imageBounds;
    }
    return obj;
  }

  getImageBounds(imageBounds) {
    const anchors = [];
    anchors.push([imageBounds[1][0], imageBounds[0][1]]);
    anchors.push([imageBounds[1][0], imageBounds[1][1]]);
    anchors.push([imageBounds[0][0], imageBounds[1][1]]);
    anchors.push([imageBounds[0][0], imageBounds[0][1]]);
    return anchors;
  }

  getForecastFileTimeStamp(primarySource, secondarySource) {
    // if (primarySource === 'WEATHER_NEXT_GRAPH') {
    //   const refDate = new Date();
    //   refDate.setHours(8, 30, 0, 0);
    //   return Promise.resolve(refDate.getTime().toString());
    // }
    let url = ProjConstants.API_URL + ProjConstants.API_MAPPING['FORECASTFILES'] + '/' + primarySource + "/" + secondarySource;
    let promise = new Promise((resolve, reject) => {
      this.http.get(url).subscribe(data => {
        let max = data[primarySource];
        for (let source in data) {
          if (max == undefined || Number(data[source]) > max) {
            max = Number(data[source])
          }
        }
        resolve(max.toString())
      }, error => {
        reject(error);
      })
    })
    return promise
  }

  // -7D's "Observed on" label (rainfall-forecast-map.service.ts) — { lastUpdated: <epoch-ms> }.
  getObservedRainfallLastUpdated() {
    const url = environment.url.HEATWAVE_STAGING_API_URL + 'observedRainfall/lastUpdated';
    return this.http.get<any>(url);
  }

  getCurrentBlockTs(dayStartTs) {
    let currentTs = new Date().getTime()
    let blockTs = dayStartTs;
    let timeStamp;
    for (let i = 0; i < 8; i++) {
      if (blockTs <= currentTs) {
        timeStamp = blockTs;
      } else {
        break;
      }
      blockTs = blockTs + 10800000;
    }
    return timeStamp

  }

  // convertTimestampToFullBlockValue(ts) {
  //   ts = Number(ts);
  //   let starthours = new Date(ts).getHours();
  //   const startminutes = new Date(ts).getMinutes();
  //   let startamPm = 'A.M';
  //   if (starthours > 12) {
  //     starthours = starthours - 12;
  //     startamPm = 'P.M';
  //   }
  //   let endhours = new Date(ts + ProjConstants.MILLS_IN_3HOURS).getHours();
  //   const endminutes = new Date(ts + ProjConstants.MILLS_IN_3HOURS).getMinutes();
  //   let endamPm = 'A.M';
  //   if (endhours > 12) {
  //     endhours = endhours - 12;
  //     endamPm = 'P.M';
  //   }
  //   return starthours.toString() + ':' + startminutes.toString() + ' ' + startamPm + ' - ' + endhours.toString() + ':' + endminutes.toString() + ' ' + endamPm;
  // }

  // convertTimestampToBlockValue(ts) {
  //   ts = Number(ts);
  //   let hours = new Date(ts).getHours();
  //   const minutes = new Date(ts).getMinutes();
  //   let amPm = 'A.M';
  //   if (hours > 12) {
  //     hours = hours - 12;
  //     amPm = 'P.M';
  //   }
  //   return hours.toString() + ':' + minutes.toString() + ' ' + amPm;
  // }

  // getCurrentBlockIndex(blocks) {
  //   const currentTime = new Date().getTime();
  //   let currentBlockIndex: any = 0; // Default value if not found

  //   for (const index in blocks) {
  //     const blockStartTime = blocks[index].timeStamp;
  //     const blockEndTime = blockStartTime + 3 * 60 * 60 * 1000; // 3 hours in milliseconds

  //     if (currentTime >= blockStartTime && currentTime < blockEndTime) {
  //       currentBlockIndex = index;
  //       break;
  //     }
  //   }

  //   return currentBlockIndex;
  // }
  convertTimestampToFullBlockValue(ts) {
    return this.utils.formatTo12HourRange(ts, 3);
  }

  convertTimestampToBlockValue(ts) {
    return this.utils.formatTo12Hour(ts);

  }
  getCurrentBlockIndex(blocks) {
    const currentTime = new Date().getTime();
    let currentBlockIndex: any = 0; // Default value if not found

    for (const index in blocks) {
      const blockStartTime = blocks[index].timeStamp;
      const blockEndTime = blockStartTime + 3 * 60 * 60 * 1000; // 3 hours in milliseconds

      if (currentTime >= blockStartTime && currentTime < blockEndTime) {
        currentBlockIndex = index;
        break;
      }
    }

    return currentBlockIndex;
  }

  deepClone(object: any): any {
    if (isEmptyField(object)) {
      return;
    }
    if (Array.isArray(object)) {
      return object.slice();
    }
    const cloneObj: any = {};
    const attributes = Object.keys(object);
    for (const attribute of attributes) {
      const property = object[attribute];

      if (typeof property === 'object') {
        cloneObj[attribute] = this.deepClone(property);
      } else {
        cloneObj[attribute] = property;
      }
    }
    return cloneObj;
  }

  getUpdatePostData(queryParams, isChart = false) {
    this.sourceSelection = queryParams['source'] == 'IMD_SHORT_RANGE' ? 'IMD_LONG_RANGE' : queryParams['source']
    let postData: any
    let allLocations = (queryParams.location && typeof queryParams.location === 'string') ? queryParams.location.split('&') : [];
    let stateUUID;
    if (queryParams.locType == 'DISTRICT') {
      stateUUID = allLocations[1] ? allLocations[1].split('##')[1] : (queryParams.pUUID || queryParams.sUUID);
    }
    let hie: any = queryParams['view'] === 'Basin' ? BASIN_HIE : AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
    let currentLevelIndex = hie.indexOf(queryParams.locType);
    let currentLevel: any = hie[currentLevelIndex + 1] || queryParams.locType;
    const refDate = new Date();
    refDate.setHours(8, 30, 0, 0);
    let refTs = (queryParams.forecastLength == 'weekly' || queryParams.forecastLength == 'monthly' || queryParams.forecastLength == 'seasonal') ? (queryParams.refTs || refDate.getTime()) : refDate.getTime();

    let ecmwfType = isNullOrUndefined(queryParams['sourceType']) ? queryParams.forecastLength + '_mean' : queryParams.forecastLength + '_' + queryParams['sourceType']

    let isExtended = (queryParams['source'] == 'ECMWF') && (queryParams.forecastLength == 'weekly' || queryParams.forecastLength == 'monthly' || queryParams.forecastLength == 'seasonal');

    let primarySource = isExtended ? (ECMWF_SOURCE_TYPES[ecmwfType] || queryParams['source']) : queryParams['source'];
    let forecastLength = (queryParams.forecastLength == 'weekly' || queryParams.forecastLength == 'monthly') ? queryParams.forecastLength + '_ext' : queryParams.forecastLength;
    let component = (isChart && isExtended) ? primarySource : "RAINFALL";
    const currentView = queryParams['view'] || 'Admin';
    let sUUID;
    if (currentView === 'Basin') {
      sUUID = queryParams.pEUUID || queryParams.pUUID;
    } else {
      sUUID = queryParams.pUUID;
    }

    postData = {
      "primarySource": primarySource,
      "secondarySource": this.sourceSelection,
      "sUUID": sUUID,
      "component": component,
      "pUUID": queryParams.pUUID,
      "pType": queryParams.locType,
      "forecastLength": forecastLength == 'deterministic' ? "10" : forecastLength,
      "view": currentView,
      "cType": currentLevel,
      "refTs": refTs
    };

    if (queryParams.locType == 'DISTRICT' || queryParams.locType == 'BLOCK') {
      postData["parentUUID"] = stateUUID;
      postData["sUUID"] = stateUUID;
      postData["locationType"] = queryParams.locType;
    }

    if (queryParams.forecastLength == 'seasonal') {
      postData["seasonMonths"] = [6, 7, 8, 9];
    }

    if (queryParams.forecastLength == 'deterministic') {
      postData["windowLength"] = 14
    }

    if (isChart && queryParams.forecastLength == 'seasonal') {
      postData["colorPallette"] = "default";
      postData["parentUUID"] = queryParams.pUUID;
      postData["component"] = (queryParams['sourceType'] || 'mean') == 'anomaly'
        ? ECMWF_SOURCE_TYPES['seasonal_anomaly']
        : ECMWF_SEASONAL_COMPONENT_TYPES['mean'];
      if (currentView === 'Basin' && queryParams.pEUUID) {
        postData["sUUID"] = queryParams.pEUUID;
      }
    }

    return postData;
  }

  // Rainfall (Deterministic + Extended) — weekly-accumulated table payload (ECMWF_EXT_WEEKLY_SUM).
  // Mirrors the pType/cType hierarchy derivation used by getUpdatePostData() above.
  getWeeklyAccumulatedPostData(queryParams: any) {
    let hie: any = queryParams['view'] === 'Basin' ? BASIN_HIE : AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
    let currentLevelIndex = hie.indexOf(queryParams.locType);
    let cType: any = hie[currentLevelIndex + 1] || queryParams.locType;
    return {
      pEUUID: queryParams.pEUUID,
      pUUID: queryParams.pUUID,
      pType: queryParams.locType,
      cType: cType,
      source: 'ECMWF_EXT_WEEKLY_SUM'
    };
  }

  getSeasonalChartPostData(queryParams) {
    this.sourceSelection = queryParams['source'] == 'IMD_SHORT_RANGE' ? 'IMD_LONG_RANGE' : queryParams['source']
    let hie: any = queryParams['view'] === 'Basin' ? BASIN_HIE : AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
    let currentLevelIndex = hie.indexOf(queryParams.locType);
    let currentLevel: any = hie[currentLevelIndex + 1] || queryParams.locType;
    const refDate = new Date();
    refDate.setHours(8, 30, 0, 0);
    const refTs = queryParams.refTs || refDate.getTime();
    const selectedSourceType = queryParams['sourceType'] || 'mean';
    const primarySource = ECMWF_SOURCE_TYPES['seasonal_' + selectedSourceType];
    const component = (selectedSourceType == 'anomaly')
      ? primarySource
      : ECMWF_SEASONAL_COMPONENT_TYPES['mean'];
    const currentView = queryParams['view'] || 'Admin';
    const sUUID = (currentView === 'Basin' && queryParams.pEUUID)
      ? queryParams.pEUUID
      : queryParams.pEUUID || queryParams.pUUID;

    return {
      "primarySource": primarySource,
      "secondarySource": this.sourceSelection,
      "sUUID": sUUID,
      "component": component,
      "locationType": currentLevel,
      "pType": queryParams.locType,
      "parentUUID": queryParams.pUUID,
      "view": currentView,
      "colorPallette": "default",
      "refTs": refTs,
      "seasonMonths": [6, 7, 8, 9]
    };
  }

  getGooglePostData(queryParams, isChart = false) {
    let hie: any = queryParams['view'] === 'Basin' ? BASIN_HIE : AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
    let currentLevel: any = hie[hie.indexOf(queryParams.locType) + 1] || queryParams.locType;
    if (queryParams['view'] === 'Basin') {
      currentLevel = hie[hie.indexOf(queryParams.locType) + 1] || queryParams.locType;
    }
    const slotHours = [5, 11, 17, 23]; // each slot is at HH:30
    const now = new Date();
    const refDate = new Date(now);

    let selectedHour: number | null = null;
    for (let i = slotHours.length - 1; i >= 0; i--) {
      const h = slotHours[i];
      const slotTime = new Date(now);
      slotTime.setHours(h, 30, 0, 0);
      if (now.getTime() >= slotTime.getTime()) {
        selectedHour = h;
        break;
      }
    }

    if (selectedHour === null) {
      // Before 5:30 AM today — fall back to yesterday's 23:30 slot
      refDate.setDate(refDate.getDate() - 1);
      refDate.setHours(23, 30, 0, 0);
    } else {
      refDate.setHours(selectedHour, 30, 0, 0);
    }
    let refTs = refDate.getTime();

    let allLocations = queryParams.location ? queryParams.location.split('&') : [];
    let stateUUID;
    if (queryParams.locType == 'DISTRICT') {
      stateUUID = allLocations[1] ? allLocations[1].split('##')[1] : queryParams.pUUID;
    }

    const currentView = queryParams['view'] || 'Admin';
    // if (isChart && queryParams.locType === 'BLOCK') {
    //   let districtUUID = allLocations[2] ? allLocations[2].split('##')[1] : queryParams.pUUID;
    //   return {
    //     "parentUUID": districtUUID,
    //     "primarySource": "WEATHER_NEXT_GRAPH",
    //     "secondarySource": "WEATHER_NEXT_GRAPH",
    //     "view": "Admin",
    //     "pType": "DISTRICT",
    //     "sUUID": districtUUID,
    //     "component": "RAINFALL",
    //     "locationType": "DISTRICT",
    //     "forecastLength": queryParams.forecastLength,
    //     "refTs": refTs,
    //     "pUUID": stateUUID,
    //     "cType": "BLOCK"
    //   };
    // }

    let postData: any = {
      "primarySource": "WEATHER_NEXT_GRAPH",
      "secondarySource": "WEATHER_NEXT_GRAPH",
      "sUUID": isChart ? queryParams.pEUUID : queryParams.pUUID,
      "pUUID": queryParams.pUUID,
      "component": "RAINFALL",
      "pType": queryParams.locType,
      "cType": currentLevel,
      "forecastLength": queryParams.forecastLength,
      "view": currentView,
      "refTs": refTs
    }
    if (queryParams['view'] === 'Admin') {
      postData["forecastLength"] = queryParams.forecastLength;
    }

    if (isChart) {
      postData["parentUUID"] = queryParams.pUUID;
      postData["locationType"] = currentLevel;
      delete postData["pUUID"];
      delete postData["cType"];
    }

    if (queryParams.locType == 'DISTRICT' && !isChart) {
      postData["parentUUID"] = stateUUID;
      postData["sUUID"] = isChart ? queryParams.pEUUID : stateUUID;
    }

    return postData;
  }

  getUpdatePostDataForMean(queryParams) {
    this.sourceSelection = queryParams['source'] == 'IMD_SHORT_RANGE' ? 'IMD_LONG_RANGE' : queryParams['source']
    let postData: any
    let allLocations = queryParams.location ? queryParams.location.split('&') : [];
    let stateUUID;
    if (queryParams.locType == 'DISTRICT') {
      stateUUID = allLocations[1] ? allLocations[1].split('##')[1] : queryParams.pUUID;
    }
    let hie: any = queryParams['view'] === 'Basin' ? BASIN_HIE : AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
    let currentLevel: any = hie[hie.indexOf(queryParams.locType) + 1] || queryParams.locType;
    if (queryParams['view'] === 'Basin') {
      currentLevel = hie[hie.indexOf(queryParams.locType) + 1] || queryParams.locType;
    }
    let firstLevel: any = hie[hie.indexOf(queryParams.locType) - 1];
    const refDate = new Date();
    refDate.setHours(8, 30, 0, 0);
    let refTs = refDate.getTime();
    let ecmwfType = 'monthly_anomaly_per'
    const currentView = queryParams['view'] || 'Admin';
    postData = {
      "parentUUID": stateUUID,
      "primarySource": ECMWF_SOURCE_TYPES[ecmwfType],
      "secondarySource": this.sourceSelection,
      "view": currentView,
      "pType": queryParams.locType,
      "sUUID": stateUUID,
      "component": "RAINFALL",
      "locationType": queryParams.locType,
      "forecastLength": 'monthly_ext',
      "refTs": refTs,
      "pUUID": queryParams.pUUID,
      "cType": currentLevel
    }
    return postData;
  }

  getMonsoonPostData(queryParams) {
    const currentView = queryParams['view'] || 'Admin';
    const postData = {
      "primarySource": "ECMWF_EXT_MONTHLY_MEAN",
      "secondarySource": queryParams.source,
      "sUUID": queryParams.pUUID,
      "component": "RAINFALL",
      "pUUID": queryParams.pUUID,
      "pType": "STATE",
      "forecastLength": "seasonal",
      "view": currentView,
      "cType": "DISTRICT",
      "refTs": new Date().getTime(),
      "seasonMonths": [6, 7, 8, 9]
    }
    return postData
  }

  getChartData(formattedChartData: any[], params: any): any {
    const currentView = params['view'] || 'Admin';
    let locType = '';

    if (currentView === 'Basin') {
      if (params.locType === 'COUNTRY') {
        locType = 'Basins';
      } else if (params.locType === 'BASINV2') {
        locType = 'Sub Basins';
      } else {
        locType = 'Locations';
      }
    } else {
      let hie: any = AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
      let firstLevel: any = hie[hie.indexOf(params.locType) + 1];
      locType = firstLevel ? firstLevel.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Locations';
    }
    // let locType = params['locType'] !== 'STATE' ? 'Districts' : 'Blocks';
    let hie: any = AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
    let currentLevel: any = hie[hie.indexOf(params.locType) - 1];
    let firstLevel: any = hie[hie.indexOf(params.locType) + 1];

    if (!formattedChartData || formattedChartData.length === 0) {
      return {}; // return empty if no data
    }

    // Extract timestamps (keys except "Rainfall Band")
    // const timestamps = Object.keys(formattedChartData[0]).filter(key => key !== "Rainfall Band");
    const timestamps = Object.keys(formattedChartData[0]).filter(key => key !== "Rainfall Band").sort((a, b) => {
      if (a === '24hr' && b !== '24hr') { return 1; }
      if (a !== '24hr' && b === '24hr') { return -1; }
      return Number(a) - Number(b);
    });
    if (params.forecastLength === '-7D') {
      // -7D "Category Wise Rainfall Distribution" chart only: latest date first, oldest last —
      // matching the Infobox table's same reversal (getLast7DaysTableData()). `categories` and
      // every series' `data` below are both derived from this same `timestamps` array (mapped
      // in the same order), so reversing it once here keeps each band's values correctly paired
      // with their date. The play bar / map date order comes from a separate method
      // (getLast7DaysTimeStamps()) and stays chronological, unaffected by this.
      timestamps.reverse();
    }

    // Format timestamps to readable time like "08:30"
    // const categories = timestamps.map(ts => {
    //   const date = new Date(Number(ts));
    //   const hours = date.getHours().toString().padStart(2, '0');
    //   const minutes = date.getMinutes().toString().padStart(2, '0');
    //   return `${hours}:${minutes}`;
    // });
    let categories: any;
    if (params.forecastLength == '24') {
      // if(entry.blockLength == '3hr'){
      categories = timestamps.map(ts => {
        if (ts === "24hr") {
          return "24hr";
        } else {
          const date = new Date(Number(ts));
          // const hours = date.getHours().toString().padStart(2, '0');
          // const minutes = date.getMinutes().toString().padStart(2, '0');
          // return `${hours}:${minutes}`;
          return this.utils.formatTo12Hour(date);
        }
      });
      // }
    } else if (params.forecastLength == '10' || params.forecastLength == '7' || params.forecastLength == 'deterministic') {
      // Daily forecast (IMD 10D or Google 7D) — format as dd/MM/yyyy
      categories = timestamps.map(ts => {
        const date = new Date(Number(ts));
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      });
    } else if (params.forecastLength == '-7D') {
      categories = timestamps.map(ts => {
        const date = new Date(Number(ts));
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${day}/${month}`;
      });
    } else if (params.forecastLength == 'weekly' || params.forecastLength == 'monthly' || params.forecastLength == 'seasonal') {
      categories = timestamps.map(ts => {
        const date = params.forecastLength == 'weekly' ? this.formatDateRange(ts) : params.forecastLength == 'seasonal' ? this.getSeasonalName(ts) : this.getMonthName(ts)
        return date;
      });
    }

    const ecmwfType = isNullOrUndefined(params['sourceType']) ? params.forecastLength + '_mean' : params.forecastLength + '_' + params['sourceType']
    const isECMWFExtended = params.source == 'ECMWF' && (params.forecastLength == 'weekly' || params.forecastLength == 'monthly' || params.forecastLength == 'seasonal');
    // Rainfall (Deterministic + Extended) — ECMWF_EXT_WEEKLY_SUM has its own band/color scheme
    // (ECMWF_EXT_WEEKLY_SUM_LEGEND), distinct from the standard ECMWF weekly mean/anomaly one
    // isECMWFExtended would otherwise select — must be checked first since both conditions
    // are true at once (source ECMWF, forecastLength weekly) while this mode is active.
    const isExtendedWeeklySum = params.forecastLength == 'weekly' && params['rainfallMode'] == 'extended';
    const standardBandConfig = this.getStandardRainfallBandConfig(params.forecastLength, params.source);
    let chartColorMap = isExtendedWeeklySum ? this.getColorMapForExtendedWeeklyChart() : isECMWFExtended ? this.getColorMapForECMWFChart(ecmwfType) : standardBandConfig.colorMap
    let labelMap = (isExtendedWeeklySum || isECMWFExtended) ? {} : standardBandConfig.labelMap
    let displayChartData = formattedChartData;

    if (ecmwfType == 'seasonal_anomaly') {
      const seasonalAnomalyConfig = this.getSeasonalAnomalyDisplayConfig(formattedChartData, timestamps);
      displayChartData = seasonalAnomalyConfig.chartData;
      chartColorMap = seasonalAnomalyConfig.colorMap;
      labelMap = seasonalAnomalyConfig.labelMap;
    }
    // console.log(chartColorMap, "chartColorMap");

    // Dynamically generate series for each Rainfall Band
    // console.log(formattedChartData,"formayyed chart data");

    const series = displayChartData.map(item => ({
      // name: item["Rainfall Band"],
      name: labelMap[item["Rainfall Band"]] || item["Rainfall Band"],
      type: 'column',
      data: timestamps.map(ts => item[ts] || 0),
      color: chartColorMap[item["Rainfall Band"]] || "#808080" // default color if not found
    }));

    const chartOptions = {
      chart: {
        type: 'column',
        backgroundColor: '#000000'
      },
      title: {
        text: '',
        style: { color: '#ffffff' }
      },
      xAxis: {
        categories: categories,
        labels: {
          style: {
            color: '#9aa2a7ff',
            fontFamily: 'poppins, sans-serif',
          }
        },
        crosshair: true
      },
      yAxis: {
        min: 0,
        title: {
          text: `No of ${locType}`,
          style: {
            color: '#9aa2a7ff',
            fontFamily: 'poppins, sans-serif',
          }
        },
        labels: {
          style: {
            color: '#9aa2a7ff',
            fontFamily: 'poppins, sans-serif',
          }
        },
        gridLineColor: '#808080'
      },
      legend: {

        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemMarginTop: 4,
        itemMarginBottom: 4,
        symbolRadius: 0,
        itemStyle: {
          color: '#9aa2a7ff',
          fontFamily: 'poppins, sans-serif',
          fontSize: '8px',
          fontWeight: 'normal'
        }
      },
      tooltip: {
        headerFormat: '<b>{point.x}</b><br/>',
        pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: true
          },
          borderWidth: 0.1,
          borderColor: '#000'
        }
      },
      series: series,
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      }
    };

    return chartOptions;
  }

  getTimeStampsWithHeatMaps(response, forecastLength) {
    if (response) {
      const blocks = {};
      let keysList = Object.keys(response);
      for (let i = 0; i < keysList.length; i++) {
        blocks[i] = {};
        blocks[i]['timeStamp'] = keysList[i];
        blocks[i]['timeLineName'] = {};
        blocks[i]['timeLineName']['toolTip'] = forecastLength == 'weekly' ? this.formatDateRange(blocks[i]['timeStamp']) : forecastLength == 'seasonal' ? this.getSeasonalName(blocks[i]['timeStamp']) : this.getMonthName(blocks[i]['timeStamp']);
        blocks[i]['timeLineName']['label'] = forecastLength == 'weekly' ? this.formatDateRange(blocks[i]['timeStamp']) : forecastLength == 'seasonal' ? this.getSeasonalName(blocks[i]['timeStamp']) : this.getMonthName(blocks[i]['timeStamp']);
        blocks[i]['timeLinePos'] = i;
        blocks[i]['url'] = response[keysList[i]]['heatMapImage'];
        blocks[i]['heatMapImageBound'] = response[keysList[i]]['heatMapImageBound'];
      }
      return blocks;
    }

  }


  getHeatMapImageBounds(heatmapimageBounds: any) {
    let latlongs: any = [];
    let imageBounds: any = []

    latlongs.push(heatmapimageBounds['southWest']['latitude']);
    latlongs.push(heatmapimageBounds['southWest']['longitude']);
    imageBounds.push(latlongs);
    latlongs = [];
    latlongs.push(heatmapimageBounds['northEast']['latitude']);
    latlongs.push(heatmapimageBounds['northEast']['longitude']);
    imageBounds.push(latlongs);
    return imageBounds;
  }

  formatDate(yyyymmdd) {
    const dateStr = yyyymmdd.toString();
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${day}/${month}/${year}`;
  }

  formatYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }


  getRfForecastChartData(formattedChartData: any[]): any {
    if (!formattedChartData || formattedChartData.length === 0) {
      return {};
    }
    let timestamps = Object.keys(formattedChartData[0]).filter(key => key !== "Rainfall Band").sort((a, b) => Number(a) - Number(b));
    let categories: any;
    categories = timestamps.map(ts => {
      if (ts === "24hr") {
        return "24hr";
      } else {
        //  const date = new Date(Number(ts));
        // const hours = date.getHours().toString().padStart(2, '0');
        // const minutes = date.getMinutes().toString().padStart(2, '0');
        // return `${hours}:${minutes}`;
        return this.utils.formatTo12Hour(new Date(Number(ts)));
      }
    });
    const colorMap: { [key: string]: string } = {
      "no rainfall": "#ffffff",
      "very light rainfall": "#C3FDCA",
      "light rainfall": "#00FE08",
      "moderate rainfall": "#038500",
      "heavy rainfall": "#FDBFC9",
      "very heavy rainfall": "#FC0300",
      "extremely heavy rainfall": "#610301"
    };

    // Dynamically generate series for each Rainfall Band
    const series = formattedChartData.map(item => ({
      name: item["Rainfall Band"],
      // type: 'column',
      type: 'spline',
      data: timestamps.map(ts => item[ts] || 0),
      color: colorMap[item["Rainfall Band"]] || "#808080" // default color if not found
    }));

    const chartOptions = {
      chart: {
        // type: 'column',
        type: 'spline',
        height: 280,
        backgroundColor: '#404040'
      },
      title: {
        text: '',
        style: { color: '#ffffff' }
      },
      xAxis: {
        categories: categories,
        labels: {
          style: {
            color: '#9aa2a7ff',
            fontFamily: 'poppins, sans-serif',
          }
        },
        crosshair: true
      },
      yAxis: {
        min: 0,
        title: {
          text: `No of States`,
          style: {
            color: '#9aa2a7ff',
            fontFamily: 'poppins, sans-serif',
          }
        },
        labels: {
          style: {
            color: '#9aa2a7ff',
            fontFamily: 'poppins, sans-serif',
          }
        },
        gridLineColor: '#808080'
      },
      legend: {

        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemMarginTop: 4,
        itemMarginBottom: 4,
        symbolRadius: 0,
        itemStyle: {
          color: '#9aa2a7ff',
          fontFamily: 'poppins, sans-serif',
          fontSize: '8px',
          fontWeight: 'normal'
        },
        title: {
          text: '',
          style: { color: '#ffffff', fontWeight: 'bold' }
        }
      },
      tooltip: {
        shared: true,
        backgroundColor: '#1e1e1e',
        borderColor: '#666',
        style: { color: '#fff' },
        headerFormat: '<b>{point.key}</b><br/>',
        pointFormat:
          '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y}</b><br/>'
      },
      // plotOptions: {
      //   column: {
      //     stacking: 'normal',
      //     // dataLabels: {
      //     //   enabled: true
      //     // }
      //   }
      // },
      plotOptions: { spline: { dataLabels: { enabled: false }, enableMouseTracking: true } },
      // series: series.map(s => ({ ...s, type: 'spline' })),
      series: series,
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      }
    };

    return chartOptions;
  }

  getSeasonalAnomalyDisplayConfig(formattedChartData: any[], timestamps: any[]) {
    const legendConfigList = RAINFALL_LEGEND_LIST[AgricultureMapConstants.LOCATIONS.INDIA][ECMWF_SEASONAL_COMPONENT_TYPES['anomaly']]['colorValues'] || [];
    const colorMap = {};
    const labelMap = {};
    const chartData = [];

    for (let index = 0; index < legendConfigList.length; index++) {
      const legendItem = legendConfigList[index];
      colorMap[legendItem.chartText] = legendItem.backgroundColor;
      labelMap[legendItem.chartText] = legendItem.text;

      const chartItem = { 'Rainfall Band': legendItem.chartText };
      for (let tsIndex = 0; tsIndex < timestamps.length; tsIndex++) {
        chartItem[timestamps[tsIndex]] = 0;
      }
      chartData.push(chartItem);
    }

    for (let index = 0; index < formattedChartData.length; index++) {
      const item = formattedChartData[index];
      const sourceBand = item['Rainfall Band'];
      const targetBand = this.mapSeasonalAnomalySourceToDisplayBand(sourceBand, legendConfigList);
      const targetItem = chartData.find(chartItem => chartItem['Rainfall Band'] == targetBand);

      if (!targetItem) {
        continue;
      }

      for (let tsIndex = 0; tsIndex < timestamps.length; tsIndex++) {
        const ts = timestamps[tsIndex];
        targetItem[ts] = (targetItem[ts] || 0) + (Number(item[ts]) || 0);
      }
    }

    return { chartData: chartData, colorMap: colorMap, labelMap: labelMap };
  }

  mapSeasonalAnomalySourceToDisplayBand(sourceBand: string, legendConfigList: any[]) {
    const sourceRange = this.parseChartBandRange(sourceBand);
    if (!sourceRange) {
      return sourceBand;
    }

    let selectedBand = legendConfigList[0] ? legendConfigList[0].chartText : sourceBand;
    let maxOverlap = -1;

    for (let index = 0; index < legendConfigList.length; index++) {
      const legendItem = legendConfigList[index];
      const targetRange = this.parseChartBandRange(legendItem.chartText);
      const overlap = this.getRangeOverlapScore(sourceRange, targetRange);

      if (overlap > maxOverlap) {
        maxOverlap = overlap;
        selectedBand = legendItem.chartText;
      }
    }

    return selectedBand;
  }

  parseChartBandRange(band: string) {
    if (!band) {
      return null;
    }

    const normalizedBand = band.toString().replace(/\s+/g, '');

    if (normalizedBand.charAt(0) == '<') {
      return { min: Number.NEGATIVE_INFINITY, max: Number(normalizedBand.substring(1)) };
    }

    if (normalizedBand.charAt(0) == '>') {
      return { min: Number(normalizedBand.substring(1)), max: Number.POSITIVE_INFINITY };
    }

    const rangeMatch = normalizedBand.match(/^(-?\d+)-(-?\d+)$/);
    if (!rangeMatch) {
      return null;
    }

    return { min: Number(rangeMatch[1]), max: Number(rangeMatch[2]) };
  }

  getRangeOverlapScore(sourceRange: any, targetRange: any) {
    if (!sourceRange || !targetRange) {
      return -1;
    }

    const overlapStart = Math.max(sourceRange.min, targetRange.min);
    const overlapEnd = Math.min(sourceRange.max, targetRange.max);

    if (overlapEnd > overlapStart) {
      if (!isFinite(overlapStart) || !isFinite(overlapEnd)) {
        return Number.MAX_SAFE_INTEGER;
      }
      return overlapEnd - overlapStart;
    }

    const sourceMid = this.getRangeMidpoint(sourceRange);
    const targetMid = this.getRangeMidpoint(targetRange);
    return -Math.abs(sourceMid - targetMid);
  }

  getRangeMidpoint(range: any) {
    if (range.min === Number.NEGATIVE_INFINITY) {
      return range.max - 100;
    }

    if (range.max === Number.POSITIVE_INFINITY) {
      return range.min + 100;
    }

    return (range.min + range.max) / 2;
  }

  getStandardRainfallBandConfig(forecastLength: string, source: string) {
    const legendKey = forecastLength == '24' && source == 'WEATHER_NEXT_GRAPH' ? 'RAINFALL_LEGEND_GOOGLE_WEATHER' : (forecastLength == '24' ? 'RAINFALL_LEGEND' : '7DAYS_RAINFALL_LEGEND');
    const unit = forecastLength == '24' ? 'mm/hr' : 'mm/day';
    const bandKeys = [
      'No Rain',
      'Very Light',
      'Light',
      'Moderate',
      'Heavy',
      'Very Heavy',
      'Extreme',
      'Cloud Burst'
    ];
    const legendConfigList = RAINFALL_LEGEND_LIST[AgricultureMapConstants.LOCATIONS.INDIA][legendKey]['colorValues'] || [];
    const colorMap = {};
    const labelMap = {};

    for (let index = 0; index < bandKeys.length; index++) {
      const bandKey = bandKeys[index];
      const legendItem = legendConfigList[index];
      if (!legendItem) {
        continue;
      }

      const rangeLabel = forecastLength == '24'
        ? legendItem.text.trim()
        : legendItem.text.split(' (')[0].trim();

      colorMap[bandKey] = legendItem.backgroundColor;
      labelMap[bandKey] = bandKey + ' (' + rangeLabel + ' ' + unit + ')';
    }

    return { colorMap: colorMap, labelMap: labelMap };
  }

  setChartData(data, ProjConstants) {
    const keys = Object.keys(data);
    const formattedChartData = [];
    for (const key of keys) {
      const obj = {};
      for (const item of Object.keys(data[key])) {

        if (item == '6hr') {
          // IMD 24H: 6-hour blocks — keep timestamps as keys
          const timeStamps = Object.keys(data[key][item]);
          for (const timestamp of timeStamps) {
            obj[timestamp] = data[key][item][timestamp];
          }
        } else if (item == '24hr') {
          // IMD 24H: cumulative 24hr block
          const timeStamps = Object.keys(data[key][item]);
          for (const timestamp of timeStamps) {
            obj['24hr'] = data[key][item][timestamp];
          }
        } else if (item == 'day') {
          // WeatherNext / GOOGLE: daily blocks — keep timestamps as keys
          const timeStamps = Object.keys(data[key][item]);
          for (const timestamp of timeStamps) {
            obj[timestamp] = data[key][item][timestamp];
          }
        } else {
          // Fallback: treat as raw timestamp map
          const timeStamps = Object.keys(data[key][item]);
          for (const timestamp of timeStamps) {
            obj[timestamp] = data[key][item][timestamp];
          }
        }
      }
      obj[ProjConstants.CHART_DATA_COLUMNS['RAINFALL']] = key;
      formattedChartData.push(obj);
    }
    return formattedChartData;
  }

  setRainfallcolor(value1) {
    let value: string = value1;
    value = value.toString().replace(",", "").trim().toLowerCase().replace(' rainfall', '');
    let obj;
    if (value == 'no') {
      obj = { 'textcolor': '#5e594e', 'bgcolor': '#ffffff', 'col': 'color1' }
    }
    else if (value == 'very light') {
      obj = { 'textcolor': '#5e594e', 'bgcolor': '#C3FDCA', 'col': 'color2' }
    }
    else if (value == 'light') {
      obj = { 'textcolor': '#5e594e', 'bgcolor': '#00FE08', 'col': 'color3' }
    }
    else if (value == 'moderate') {
      obj = { 'textcolor': '#ffffff', 'bgcolor': '#038500', 'col': 'color4' }
    }
    else if (value == 'heavy') {
      obj = { 'textcolor': '#5e594e', 'bgcolor': '#FDBFC9', 'col': 'color5' }
    }
    else if (value == 'very heavy') {
      obj = { 'textcolor': '#5e594e', 'bgcolor': '#FC0300', 'col': 'color6' }
    }
    else if (value == 'extreme') {
      obj = { 'textcolor': '#ffffff', 'bgcolor': '#610301', 'col': 'color7' }
    }
    else {
      obj = { 'textcolor': '#ffffff', 'bgcolor': '#9932CC', 'col': 'color8' }
    }
    return obj;
  }
  getECMWFWeeklyHeatmaps(postData) {
    let url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getECMWFExtendedHeatMapsBySourceAndComponent/' + postData.source + '/' + postData.component;
    return this.http.get<any>(url)
  }
  formatDateRange(yyyymmdd: any) {

    const dateStr = yyyymmdd.toString();

    const year = Number(dateStr.substring(0, 4));
    const month = Number(dateStr.substring(4, 6)) - 1;
    const day = Number(dateStr.substring(6, 8));

    const startDate = new Date(year, month, day);

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    const format = (date: Date) => {
      const m = months[date.getMonth()];
      const d = String(date.getDate()).padStart(2, '0');
      return `${m} ${d}`;
    };

    return `${format(startDate)} - ${format(endDate)}`;
  }

  // Rainfall (Deterministic + Extended) — single source of truth for Week 1-6 Monday-Sunday
  // date ranges, derived from a runDate (yyyymmdd) returned by either the weekly-accumulated
  // table API or the weekly heatmap API. Both the infobox table headers and the map service's
  // play-bar week labels call this SAME helper so they can never disagree with each other.
  // Returns an array of 6 "Mon DD - Mon DD" strings (index 0 = Week 1 .. index 5 = Week 6);
  // returns [] if runDate isn't a valid yyyymmdd string.
  getWeeklyDateRanges(runDate: any): string[] {
    const runDateStr = (runDate || '').toString();
    if (runDateStr.length !== 8) {
      return [];
    }
    const year = Number(runDateStr.substring(0, 4));
    const month = Number(runDateStr.substring(4, 6)) - 1;
    const day = Number(runDateStr.substring(6, 8));
    const week1Monday = this.getMondayOfWeek(new Date(year, month, day));

    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    const format = (date: Date, includeYear: boolean) => {
      const m = months[date.getMonth()];
      const d = String(date.getDate()).padStart(2, '0');
      return includeYear ? `${m} ${d}, ${date.getFullYear()}` : `${m} ${d}`;
    };

    const ranges: string[] = [];
    for (let week = 1; week <= 6; week++) {
      const weekStart = new Date(week1Monday);
      weekStart.setDate(week1Monday.getDate() + (week - 1) * 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      const crossesYear = weekStart.getFullYear() !== weekEnd.getFullYear();
      ranges.push(`${format(weekStart, crossesYear)} - ${format(weekEnd, crossesYear)}`);
    }
    return ranges;
  }

  // Monday of the Mon-Sun week containing `date` — Sunday rolls back 6 days to the previous
  // Monday, any other day rolls back to that same week's Monday.
  private getMondayOfWeek(date: Date): Date {
    const day = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const isoDay = day === 0 ? 7 : day; // 1 = Monday, ..., 7 = Sunday
    const monday = new Date(date);
    monday.setDate(date.getDate() - (isoDay - 1));
    return monday;
  }

  getMonthName(yyyymm: any) {

    const dateStr = yyyymm.toString();
    const month = Number(dateStr.substring(4, 6)) - 1;

    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    return months[month];
  }

  getSeasonalName(yyyymm: any) {
    // For Monsoon (Jun-Sep), simply returning the seasonal range label
    return 'Monsoon (June-September)';
  }

  getColorMapForECMWFChart(ecmwfType) {
    const legendKey = ecmwfType.includes('seasonal')
      ? ECMWF_SEASONAL_COMPONENT_TYPES[ecmwfType.includes('anomaly') ? 'anomaly' : 'mean']
      : ECMWF_SOURCE_TYPES[ecmwfType]
    const legendConfigList = RAINFALL_LEGEND_LIST[AgricultureMapConstants.LOCATIONS.INDIA][legendKey]['colorValues']
    let colorMap: any = {}
    for (let index in legendConfigList) {
      let item = legendConfigList[index]
      // if (ecmwfType.includes('anomaly')) {
      colorMap[item.chartText] = item.backgroundColor
      // } else {
      // colorMap[item.text] = item.backgroundColor
      // }
    }
    return colorMap
  }

  // Rainfall (Deterministic + Extended) — single source of truth for the Category Wise
  // Rainfall Distribution chart's band colors, reusing ECMWF_EXT_WEEKLY_SUM_LEGEND (also used
  // for the map legend) instead of a duplicate color literal. Keyed by chartText (e.g. "0-10"),
  // matching getWeeklyChartData()'s band keys directly.
  getColorMapForExtendedWeeklyChart() {
    const legendConfigList = RAINFALL_LEGEND_LIST[AgricultureMapConstants.LOCATIONS.INDIA]['ECMWF_EXT_WEEKLY_SUM_LEGEND']['colorValues']
    let colorMap: any = {}
    for (let index in legendConfigList) {
      let item = legendConfigList[index]
      colorMap[item.chartText] = item.backgroundColor
    }
    return colorMap
  }

  getFormattedResposeWithDeviationValues(meanRes, anomalyRes) {
    if ((!anomalyRes || anomalyRes.length === 0) &&
      (!meanRes || meanRes.length === 0)) {
      return {};
    }
    if (!meanRes || meanRes.length === 0) {
      return anomalyRes;
    }
    if (!anomalyRes || anomalyRes.length === 0) {
      return {};
    }
    const meanMap = new Map();
    meanRes.forEach(meanItem => {
      const uuid = meanItem.uuid;

      meanItem.table_data.forEach(meanTd => {
        const key = `${uuid}_${meanTd.timeStamp}`;
        meanMap.set(key, meanTd);
      });
    });

    anomalyRes.forEach(anomalyItem => {
      const uuid = anomalyItem.uuid;
      anomalyItem.table_data.forEach(anomalyTd => {
        const key = `${uuid}_${anomalyTd.timeStamp}`;
        const match = meanMap.get(key);

        const anomalyRf = anomalyTd.data.rf;
        const percentage = match.data.rf;
        if (
          anomalyRf !== null && anomalyRf !== undefined &&
          percentage !== null && percentage !== undefined &&
          percentage !== 0
        ) {
          let meanrf = (anomalyRf * (100 + percentage)) / percentage;
          let dev = +percentage.toFixed(2);
          if (dev < -100) {
            dev = -100;
          }
          anomalyTd.data.meanrf = meanrf;
          anomalyTd.data.dev = dev;
        } else {
          anomalyTd.data.meanrf = null;
          anomalyTd.data.dev = null;
        }
      });
    });
    return anomalyRes;
  }

  getAnomalyPercentageChartData(postData: any) {
    const url = environment.url.HEATWAVE_STAGING_API_URL + 'weatherforecast/getAnomalyPerChartData';
    return this.http.post<any>(url, postData);
  }

  getAnomalyPerChartPostData(queryParams: any): any {
    const currentView = queryParams['view'] || 'Admin';
    const isBasin = currentView === 'Basin';
    const forecastLength = queryParams['forecastLength'];
    const countryUUID = '00cb31c7-2928-4e6d-adcc-443d478c90d6';

    let cType: string;
    if (isBasin) {
      cType = queryParams.locType === 'COUNTRY' ? 'BASINV2' : 'SUBBASINV2';
    } else {
      const adminChildMap: any = { COUNTRY: 'STATE', STATE: 'DISTRICT', DISTRICT: 'BLOCK' };
      cType = adminChildMap[queryParams.locType] || queryParams.locType;
    }

    const sUUID = queryParams.locType === 'COUNTRY' ? countryUUID : queryParams.pUUID;
    const postData: any = {
      pType: queryParams.locType,
      cType,
      sUUID,
      forecastLength: forecastLength === 'seasonal' ? 'seasonal' : 'monthly_ext',
      primarySource: 'ECMWF_EXT_MONTHLY_ANOMALY',
      colorPallette: 'default',
      refTs: 0,
      view: currentView
    };

    if (isBasin) {
      postData.parentUUID = sUUID;
    }

    return postData;
  }

  buildAnomalyPerChartOptions(response: any, queryParams: any): any {
    if (!response || typeof response !== 'object' || Object.keys(response).length === 0) return null;

    const currentView = queryParams['view'] || 'Admin';
    const forecastLength = queryParams['forecastLength'];
    let locTypeLabel: string;
    if (currentView === 'Basin') {
      locTypeLabel = queryParams.locType === 'COUNTRY' ? 'Basins' : 'Sub Basins';
    } else {
      const hie: any = AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
      const childLevel = hie[hie.indexOf(queryParams.locType) + 1];
      locTypeLabel = childLevel
        ? childLevel.toLowerCase().split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
        : 'Locations';
    }

    const categoryKeys = Object.keys(response);
    if (categoryKeys.length === 0) return null;

    const firstCatData = response[categoryKeys[0]];
    if (!firstCatData || typeof firstCatData !== 'object') return null;
    const timeData = Object.values(firstCatData)[0] as any;
    if (!timeData) return null;
    const timeKeys = Object.keys(timeData).sort();
    if (timeKeys.length === 0) return null;

    const xCategories = timeKeys.map(key =>
      forecastLength === 'seasonal' ? this.getSeasonalName(key) : this.getMonthName(key)
    );

    const legendList = RAINFALL_LEGEND_LIST[AgricultureMapConstants.LOCATIONS.INDIA]['ECMWF_EXT_MONTHLY_ANOMALY_PER']['colorValues'];
    const colorMap: any = {};
    legendList.forEach((item: any) => {
      colorMap[item.text.trim()] = item.backgroundColor;
      if (item.chartText) colorMap[item.chartText.trim()] = item.backgroundColor;
    });

    let hasData = false;
    const series = categoryKeys.map(cat => {
      const catTimeData = Object.values(response[cat])[0] as any;
      const data = timeKeys.map(k => {
        const val = catTimeData ? (catTimeData[k] || 0) : 0;
        if (val > 0) hasData = true;
        return val;
      });
      return { name: cat, type: 'column', data, color: colorMap[cat.trim()] || '#808080' };
    });

    if (!hasData) return null;

    return {
      chart: { type: 'column', backgroundColor: '#000000' },
      title: { text: '', style: { color: '#ffffff' } },
      xAxis: {
        categories: xCategories,
        labels: { style: { color: '#9aa2a7ff', fontFamily: 'poppins, sans-serif' } },
        crosshair: true
      },
      yAxis: {
        min: 0,
        title: { text: `No of ${locTypeLabel}`, style: { color: '#9aa2a7ff', fontFamily: 'poppins, sans-serif' } },
        labels: { style: { color: '#9aa2a7ff', fontFamily: 'poppins, sans-serif' } },
        gridLineColor: '#808080'
      },
      legend: {
        layout: 'horizontal', align: 'center', verticalAlign: 'bottom',
        itemMarginTop: 4, itemMarginBottom: 4, symbolRadius: 0,
        itemStyle: { color: '#9aa2a7ff', fontFamily: 'poppins, sans-serif', fontSize: '8px', fontWeight: 'normal' }
      },
      tooltip: {
        headerFormat: '<b>{point.x}</b><br/>',
        pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
      },
      plotOptions: {
        column: { stacking: 'normal', dataLabels: { enabled: true }, borderWidth: 0.1, borderColor: '#000' }
      },
      series,
      credits: { enabled: false }
    };
  }

  getDailyTrendChartData(postData: any) {
    const url = environment.url.HEATWAVE_STAGING_API_URL + 'rf/dailyTrend';
    return this.http.post<any>(url, postData);
  }

  getMonthlyTrendChartData(postData: any) {
    const url = environment.url.HEATWAVE_STAGING_API_URL + 'rf/monthlyTrend';
    return this.http.post<any>(url, postData);
  }

  buildDailyTrendChartOptions(trendDataByYear: { [year: number]: any[] }, currentYear: number): any {
    const years = Object.keys(trendDataByYear).map(y => Number(y)).sort((a, b) => a - b);
    if (!years.length) return null;

    const aboveNormalColor = '#029934';
    const belowNormalColor = '#963400';
    const normalColor = '#9aa2a7ff';
    const baseStyle = { color: '#9aa2a7ff', fontFamily: 'poppins, sans-serif' };

    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Daily view — one row per day for the fetched month, overlaid per selected year
    const referenceYear = trendDataByYear[currentYear] ? currentYear : years[years.length - 1];
    const referenceRows = (trendDataByYear[referenceYear] || []).slice().sort((a, b) => a.dt - b.dt);
    const categories = referenceRows.map(r => {
      const dtStr = String(r.dt);
      const month = Number(dtStr.substring(4, 6));
      const day = Number(dtStr.substring(6, 8));
      return MONTH_NAMES[month - 1] + ' ' + day;
    });

    const series: any[] = [{
      name: 'Normal',
      type: 'spline',
      data: referenceRows.map(r => r.nrm),
      color: normalColor,
      dashStyle: 'Dash',
      marker: { enabled: false }
    }];

    years.forEach(year => {
      const rows = (trendDataByYear[year] || []).slice().sort((a, b) => a.dt - b.dt);
      if (year === currentYear) {
        series.push({
          name: String(year) + ' (Live)',
          type: 'column',
          data: rows.map(r => ({ y: r.act, color: r.act >= r.nrm ? aboveNormalColor : belowNormalColor }))
        });
      } else {
        series.push({
          name: String(year),
          type: 'spline',
          data: rows.map(r => r.act),
          color: getTrendYearColor(year, currentYear),
          marker: { enabled: false }
        });
      }
    });

    return {
      chart: { backgroundColor: '#000000' },
      title: { text: '', style: { color: '#ffffff' } },
      xAxis: { categories, labels: { style: baseStyle }, crosshair: true },
      yAxis: {
        min: 0,
        title: { text: 'mm / day', style: baseStyle },
        labels: { style: baseStyle },
        gridLineColor: '#808080'
      },
      legend: { enabled: false },
      tooltip: {
        shared: true,
        headerFormat: '<b>{point.key}</b><br/>',
        pointFormat: '{series.name}: {point.y} mm<br/>'
      },
      plotOptions: {
        column: { borderWidth: 0.1, borderColor: '#000' },
        spline: { marker: { enabled: false } }
      },
      series,
      credits: { enabled: false },
      exporting: { enabled: false }
    };
  }

  // Monthly view — Jun-Dec of the selected year(s); rf/monthlyTrend already returns one
  // pre-aggregated row per month (dt = YYYYMM), so rows are indexed by month, not summed.
  // Months the trend API hasn't observed yet (no `act`) are filled in from the
  // weatherForecast/getTableData forecast, rendered as a faded-green bar.
  buildMonthlyTrendChartOptions(trendDataByYear: { [year: number]: any[] }, currentYear: number, forecastRowsByMonth?: { [month: number]: number }): any {
    const years = Object.keys(trendDataByYear).map(y => Number(y)).sort((a, b) => a - b);
    if (!years.length) return null;

    const normalColor = '#9aa2a7ff';
    const aboveNormalColor = '#029934';
    const belowNormalColor = '#963400';
    const aboveNormalFaded = 'rgba(2, 153, 52, 0.35)';
    const belowNormalFaded = 'rgba(150, 52, 0, 0.35)';
    const baseStyle = { color: '#9aa2a7ff', fontFamily: 'poppins, sans-serif' };

    const MONTH_NUMS = [6, 7, 8, 9, 10, 11, 12];
    const MONTH_LABELS = ['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const rowsByMonth = (rows: any[]): { [month: number]: any } => {
      const map: { [month: number]: any } = {};
      (rows || []).forEach(r => {
        const month = Number(String(r.dt).substring(4, 6));
        map[month] = r;
      });
      return map;
    };

    const rowsByYear: { [year: number]: { [month: number]: any } } = {};
    years.forEach(y => { rowsByYear[y] = rowsByMonth(trendDataByYear[y]); });

    const referenceYear = trendDataByYear[currentYear] ? currentYear : years[years.length - 1];

    const normalData = MONTH_NUMS.map(m => {
      const r = rowsByYear[referenceYear][m];
      return (r && r.nrm != null) ? +r.nrm.toFixed(1) : null;
    });

    // const liveBarData = MONTH_NUMS.map(m => {
    //   const r = rowsByYear[currentYear] && rowsByYear[currentYear][m];
    //   if (r && r.act != null) {
    //     const above = r.act >= r.nrm;
    //     return { y: +r.act.toFixed(1), color: above ? aboveNormalColor : belowNormalColor };
    //   }
    //   const forecastRf = forecastRowsByMonth && forecastRowsByMonth[m];
    //   if (forecastRf != null) {
    //     const nrm = r && r.nrm != null ? r.nrm : null;
    //     const above = nrm == null || forecastRf >= nrm;
    //     return { y: +Number(forecastRf).toFixed(1), color: above ? aboveNormalFaded : belowNormalFaded };
    //   }
    //   return null;
    // });

    // const series: any[] = [
    //   {
    //     name: String(currentYear) + ' (vs normal)',
    //     type: 'column',
    //     data: liveBarData
    //   }
    // ];

    // Split the current-year bar into two stacked segments per month:
    //  - Observed (obs): rainfall recorded so far this month → solid color
    //  - Forecast (fcst): rainfall predicted for the remainder of the month → faded color
    // Fully-elapsed months (act present, no obs/fcst split) render as one solid bar.
    // Fully-future months (no act yet) still fall back to forecastRowsByMonth, faded.
    const observedBarData: any[] = [];
    const forecastBarData: any[] = [];
    const currentMonthNum = new Date().getMonth() + 1; // 1-12, today's real month

    MONTH_NUMS.forEach(m => {
      const r = rowsByYear[currentYear] && rowsByYear[currentYear][m];

      if (m < currentMonthNum) {
        // Fully elapsed month — whole bar is observed
        if (r && r.act != null) {
          const above = r.act >= r.nrm;
          observedBarData.push({ y: +r.act.toFixed(1), color: above ? aboveNormalColor : belowNormalColor });
          forecastBarData.push(null);
        } else {
          observedBarData.push(null);
          forecastBarData.push(null);
        }
        return;
      }

      if (m === currentMonthNum) {
        // In-progress month — split into observed-so-far vs forecast-for-rest
        if (r && r.obs != null && r.fcst != null) {
          const above = r.act != null && r.nrm != null ? r.act >= r.nrm : true;
          observedBarData.push({ y: +Number(r.obs).toFixed(1), color: above ? aboveNormalColor : belowNormalColor });
          forecastBarData.push({ y: +Number(r.fcst).toFixed(1), color: above ? aboveNormalFaded : belowNormalFaded });
        } else if (r && r.act != null) {
          const above = r.act >= r.nrm;
          observedBarData.push({ y: +r.act.toFixed(1), color: above ? aboveNormalColor : belowNormalColor });
          forecastBarData.push(null);
        } else {
          observedBarData.push(null);
          forecastBarData.push(null);
        }
        return;
      }

      // m > currentMonthNum: month hasn't started yet — whole bar is forecast
      const fcstVal = (r && r.fcst != null) ? r.fcst : (forecastRowsByMonth && forecastRowsByMonth[m]);
      if (fcstVal != null) {
        const nrm = r && r.nrm != null ? r.nrm : null;
        const above = nrm == null || fcstVal >= nrm;
        observedBarData.push(null);
        forecastBarData.push({ y: +Number(fcstVal).toFixed(1), color: above ? aboveNormalFaded : belowNormalFaded });
      } else {
        observedBarData.push(null);
        forecastBarData.push(null);
      }
    });
    // Deviation % label shown above each bar, per requirement:
    //  - Past/completed month → use Observed rainfall (act)   vs Normal
    //  - Current month        → use Observed till date (obs)  vs Normal
    //  - Future/forecast month → use Forecast rainfall (fcst)  vs Normal
    // Deviation % = ((Value - Normal) / Normal) * 100
    const deviationByMonth: (number | null)[] = MONTH_NUMS.map((m, idx) => {
      const r = rowsByYear[currentYear] && rowsByYear[currentYear][m];
      const nrm = (r && r.nrm != null) ? r.nrm : normalData[idx];

      let value: number | null = null;
      if (m <= currentMonthNum) {
        value = (r && r.act != null) ? r.act : null;
      } else {
        value = (r && r.fcst != null) ? r.fcst : (forecastRowsByMonth ? forecastRowsByMonth[m] : null);
      }

      if (value == null || nrm == null || nrm === 0) { return null; }
      return +(((value - nrm) / nrm) * 100).toFixed(1);
    });

    const series: any[] = [
      {
        name: String(currentYear) + ' (Forecast)',
        type: 'column',
        data: forecastBarData,
        stack: 'live'
      },
      {
        name: String(currentYear) + ' (Observed)',
        type: 'column',
        data: observedBarData,
        stack: 'live'
      }
    ];

    years.forEach(year => {
      if (year === currentYear) { return; }
      const rows = rowsByYear[year];
      const data = MONTH_NUMS.map(m => (rows[m] && rows[m].act != null ? +rows[m].act.toFixed(1) : null));
      series.push({
        name: String(year),
        type: 'spline',
        data,
        color: getTrendYearColor(year, currentYear),
        marker: { enabled: true, radius: 4 }
      });
    });

    series.push({
      name: 'Normal (1970-2020)',
      type: 'spline',
      data: normalData,
      color: normalColor,
      dashStyle: 'Dash',
      marker: { enabled: true, radius: 3, symbol: 'circle' }
    });

    return {
      chart: { backgroundColor: '#000000' },
      title: { text: '', style: { color: '#ffffff' } },
      xAxis: { categories: MONTH_LABELS, labels: { style: baseStyle }, crosshair: true },
      // yAxis: {
      //   min: 0,
      //   title: { text: 'mm / month', style: baseStyle },
      //   labels: { style: baseStyle },
      //   gridLineColor: '#808080'
      // },
      yAxis: {
        min: 0,
        title: { text: 'mm / month', style: baseStyle },
        labels: { style: baseStyle },
        gridLineColor: '#808080',
        stackLabels: {
          enabled: true,
          useHTML: true,
          formatter: function (this: any): string {
            const dev = deviationByMonth[this.x];
            if (dev == null) { return ''; }
            const sign = dev > 0 ? '+' : '';
            const color = dev >= 0 ? aboveNormalColor : belowNormalColor;
            return `<span style="color:${color}; font-weight:600; font-family:poppins, sans-serif;">${sign}${dev}%</span>`;
          }
        }
      },
      legend: { enabled: false },
      tooltip: {
        shared: true,
        headerFormat: '<b>{point.key}</b><br/>',
        pointFormat: '{series.name}: {point.y} mm<br/>'
      },
      plotOptions: {
        series: {
          states: {
            inactive: { opacity: 1 }
          }
        },
        column: { stacking: 'normal', borderWidth: 0.1, borderColor: '#000' },
        spline: { marker: { enabled: false } }
      },
      series,
      credits: { enabled: false },
      exporting: { enabled: false }
    };
  }

  downloadRainfallForecastReport(gisMapComp: any) {
    gisMapComp.showThrobber = true;

    const isAnomaly = gisMapComp.params['sourceType'] === 'anomaly';
    const forecastLength = gisMapComp.params['forecastLength'];
    let source: string;
    let reportType: string = null;

    if (forecastLength === 'weekly') {
      source = isAnomaly ? 'ECMWF_EXT_WEEKLY_ANOMALY' : 'ECMWF_EXT_WEEKLY_MEAN';
    } else if (forecastLength === 'monthly') {
      source = isAnomaly
        ? (gisMapComp.isAnomalyPercentageActive ? 'ECMWF_EXT_MONTHLY_ANOMALY_PER' : 'ECMWF_EXT_MONTHLY_ANOMALY')
        : 'ECMWF_EXT_MONTHLY_MEAN';
    } else if (forecastLength === 'seasonal') {
      source = isAnomaly
        ? (gisMapComp.isAnomalyPercentageActive ? 'ECMWF_EXT_MONSOON_ANOMALY_PER' : 'ECMWF_EXT_MONSOON_ANOMALY')
        : 'ECMWF_EXT_MONSOON_MEAN';
    } else if (forecastLength === '24' || forecastLength === '10') {
      // Short-range: source comes from the currently selected source (IMD_LONG_RANGE / ECMWF / WEATHER_NEXT_GRAPH),
      // reportType tells the API which variant to serve. Default to 7DAY unless it's the 24hr view.
      source = gisMapComp.params['source'];
      if (forecastLength === '24') {
        reportType = '24HR';
      }
    } else if (forecastLength === '-7D') {
      source = 'MERGED_STATIONS';
    } else {
      console.warn('Report not available for forecastLength=' + forecastLength);
      gisMapComp.showThrobber = false;
      return;
    }

    const locType = gisMapComp.params['locType'];

    if (locType === 'COUNTRY') {
      gisMapComp.mapService.getRainfallForecastReportCountry(source, reportType).subscribe(
        (data) => gisMapComp.downloadReport(data, source),
        (error) => {
          console.error('Error downloading the report:', error);
          gisMapComp.showThrobber = false;
        }
      );
    } else if (locType === 'STATE' || locType === 'DISTRICT') {
      let allLocations = gisMapComp.params['location'].split('&');
      let stateUUID = allLocations[1] ? allLocations[1].split('##')[1] : null;
      let locationUUID = stateUUID;
      if (locType === 'DISTRICT') {
        const districtSegment = allLocations[2];
        locationUUID = districtSegment ? districtSegment.split('##')[1] : stateUUID;
      }
      gisMapComp.mapService.getRainfallForecastReportByLocation(locationUUID, source, reportType).subscribe(
        (data) => gisMapComp.downloadReport(data, source),
        (error) => {
          console.error('Error downloading the report:', error);
          gisMapComp.showThrobber = false;
        }
      );
    } else {
      gisMapComp.showThrobber = false;
    }
  }

  getDailyTrendDeviationPostData(queryParams: any) {
    const hie: any = AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
    const cType = hie[hie.indexOf(queryParams.locType) + 1];
    const today = new Date();

    let pEUUID = queryParams.pEUUID || queryParams.pUUID;
    if (queryParams.locType === 'DISTRICT' && queryParams.location) {
      const allLocations = queryParams.location.split('&');
      const stateSegment = allLocations[1];
      if (stateSegment && stateSegment.includes('##')) {
        pEUUID = stateSegment.split('##')[1];
      }
    }

    return {
      pEUUID: pEUUID,
      pUUID: queryParams.pUUID,
      pType: queryParams.locType,
      cType: cType,
      src: 'IMD_GRID',
      sDate: `${today.getFullYear()}0601`,
      eDate: this.formatYYYYMMDD(today)
    };
  }

  getImdStationPostData(queryParams: any): any {
    const hie: any = AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
    const cType = hie[hie.indexOf(queryParams.locType) + 1] || 'STATE';
    return {
      pEUUID: queryParams.pEUUID || queryParams.pUUID,
      pUUID: queryParams.pUUID,
      pType: queryParams.locType,
      cType: cType,
      sDate: Number(this.formatYYYYMMDD(new Date(new Date().getFullYear(), 5, 1))), // 1-Jun
      endDate: Number(this.formatYYYYMMDD(new Date())),
      source: 'ACCUMULATED_IMD_RAINFALL'
    };
  }
  getYtdDeviationColor(value: string | number): { bgcolor: string, textcolor: string } {
    const categoryColorMap: any = {
      'LE': { bgcolor: '#149AFF', textcolor: '#ffffff' }, // Large Excess
      'E': { bgcolor: '#70BBF1', textcolor: '#000000' }, // Excess
      'N': { bgcolor: '#79C66D', textcolor: '#000000' }, // Normal
      'D': { bgcolor: '#F14518', textcolor: '#ffffff' }, // Deficient
      'LD': { bgcolor: '#FCFD13', textcolor: '#000000' }, // Large Deficient
      'NR': { bgcolor: '#FEFFFF', textcolor: '#000000' }  // No Rain
    };

    if (value === null || value === undefined || value === '-' || value === '') {
      return { bgcolor: '#C1C1C1', textcolor: '#000000' }; // No Data
    }

    const asString = String(value).trim().toUpperCase();
    if (categoryColorMap[asString]) {
      return categoryColorMap[asString];
    }

    const dep = parseFloat(asString.replace('%', ''));
    if (isNaN(dep)) {
      return { bgcolor: '#C1C1C1', textcolor: '#000000' }; // No Data
    }
    if (dep <= -100) return { bgcolor: '#FEFFFF', textcolor: '#000000' }; // No Rain
    if (dep <= -60) return { bgcolor: '#FCFD13', textcolor: '#000000' }; // Large Deficient
    if (dep <= -20) return { bgcolor: '#F14518', textcolor: '#ffffff' }; // Deficient
    if (dep <= 19) return { bgcolor: '#79C66D', textcolor: '#000000' }; // Normal
    if (dep <= 59) return { bgcolor: '#70BBF1', textcolor: '#000000' }; // Excess
    return { bgcolor: '#149AFF', textcolor: '#ffffff' }; // Large Excess
  }

  getYtdInfoboxTitle(source: string, imdGridAsOnDate: string, imdStationAsOnDate: string): string {
    if (source === 'IMD_GRID' && imdGridAsOnDate) {
      return `mm/YTD - IMD GRID (as on ${this.formatDate(imdGridAsOnDate)})`;
    }
    if (source === 'IMD_STATION' && imdStationAsOnDate) {
      return `mm/YTD - IMD STATION (as on ${this.formatDate(imdStationAsOnDate)})`;
    }
    return source === 'IMD_GRID' ? 'mm/YTD - IMD_GRID' : 'mm/YTD - IMD_STATION';
  }


  getLast7DaysPostData(queryParams: any) {
    const hie: any = queryParams['view'] === 'Basin' ? BASIN_HIE : AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
    const cType: any = hie[hie.indexOf(queryParams.locType) + 1] || queryParams.locType;
    if (queryParams.locType === 'DISTRICT' && queryParams.location) {
      const allLocations = queryParams.location.split('&');
      const stateSegment = allLocations[1];
      if (stateSegment && stateSegment.includes('##')) {
        const stateUUID = stateSegment.split('##')[1];
        return {
          pUUID: queryParams.pUUID,
          pType: queryParams.locType,
          cType: cType,
          pEUUID: stateUUID,
          src: 'MERGED_STATIONS',
          aggr: 'SUM',
          classification: ['ACTUAL'],
          includeAllLoc: true,
          days: 7
        };
      }
    }
    return {
      pUUID: queryParams.pUUID,
      pType: queryParams.locType,
      cType: cType,
      pEUUID: queryParams.pUUID,
      src: 'MERGED_STATIONS',
      aggr: 'SUM',
      classification: ['ACTUAL'],
      includeAllLoc: true,
      days: 7
    };
  }
}
